import React from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { 
  CheckCircle, 
  X,
  Star,
  Zap,
  Shield,
  Headphones,
  MessageSquare,
  BarChart3,
  Clock,
  Users
} from 'lucide-react'

// 价格方案
const pricingPlans = [
  {
    name: '体验版',
    price: '免费',
    period: '',
    description: '适合个人开发者和小型项目测试',
    monthlyQuota: '100条/月',
    features: [
      '100条免费短信额度',
      '基础API接口访问',
      '标准短信模板',
      '基础发送统计',
      '邮件技术支持',
      '开发文档和示例'
    ],
    limitations: [
      '不支持自定义签名',
      '不支持营销短信',
      '发送频率限制：10条/分钟',
      '仅支持验证码短信'
    ],
    buttonText: '免费试用',
    buttonVariant: 'outline' as const,
    popular: false,
    icon: Zap
  },
  {
    name: '标准版',
    price: '0.045',
    period: '元/条',
    description: '适合中小企业日常业务需求',
    monthlyQuota: '按量计费',
    features: [
      '无月费，按实际使用计费',
      '完整API功能接口',
      '自定义短信签名',
      '验证码+通知短信',
      '实时发送状态追踪',
      '详细数据分析报表',
      '7×24小时技术支持',
      '多通道智能路由',
      '失败重发机制'
    ],
    limitations: [],
    buttonText: '立即开始',
    buttonVariant: 'default' as const,
    popular: true,
    icon: MessageSquare
  },
  {
    name: '企业版',
    price: '0.035',
    period: '元/条起',
    description: '适合大企业和高并发业务场景',
    monthlyQuota: '批量优惠',
    features: [
      '批量采购优惠价格',
      '专属客户经理服务',
      '营销短信全面支持',
      '高级数据分析和BI',
      '定制化API开发',
      '99.9% SLA服务保障',
      'VIP专线通道',
      '白名单免审服务',
      '一对一技术对接',
      '定制化解决方案'
    ],
    limitations: [],
    buttonText: '联系销售',
    buttonVariant: 'outline' as const,
    popular: false,
    icon: Shield
  }
]

// 功能对比详细
const featureComparison = [
  {
    category: '基础功能',
    features: [
      { name: '短信发送', free: true, standard: true, enterprise: true },
      { name: 'REST API接口', free: '基础版', standard: '完整版', enterprise: '定制版' },
      { name: '发送记录查询', free: '7天', standard: '90天', enterprise: '365天' },
      { name: '实时状态回调', free: false, standard: true, enterprise: true },
      { name: '数据统计分析', free: '基础', standard: '完整', enterprise: '高级+BI' }
    ]
  },
  {
    category: '高级功能',
    features: [
      { name: '自定义短信签名', free: false, standard: '5个', enterprise: '无限制' },
      { name: '营销短信发送', free: false, standard: false, enterprise: true },
      { name: '批量发送', free: '100条/次', standard: '10万条/次', enterprise: '无限制' },
      { name: '定时发送', free: false, standard: true, enterprise: true },
      { name: '发送频率限制', free: '10条/分钟', standard: '1000条/分钟', enterprise: '自定义' }
    ]
  },
  {
    category: '技术支持',
    features: [
      { name: '在线文档', free: true, standard: true, enterprise: true },
      { name: '邮件支持', free: true, standard: true, enterprise: true },
      { name: '在线客服', free: false, standard: '工作时间', enterprise: '7×24小时' },
      { name: '电话支持', free: false, standard: '工作时间', enterprise: '7×24小时' },
      { name: '专属客户经理', free: false, standard: false, enterprise: true },
      { name: '定制化开发', free: false, standard: false, enterprise: true }
    ]
  },
  {
    category: '服务保障',
    features: [
      { name: '到达率保障', free: '95%+', standard: '99%+', enterprise: '99.9%+' },
      { name: 'SLA服务协议', free: false, standard: false, enterprise: true },
      { name: '通道备份', free: '单通道', standard: '双通道', enterprise: '多通道' },
      { name: '故障响应时间', free: '24小时', standard: '4小时', enterprise: '30分钟' }
    ]
  }
]

// 常见问题
const faqs = [
  {
    question: '如何开始使用短信服务？',
    answer: '注册账户后，完成实名认证和企业认证（企业用户），即可开始使用。体验版用户可立即获得100条免费短信进行测试。'
  },
  {
    question: '支持哪些支付方式？',
    answer: '支持支付宝、微信支付、企业网银转账、对公转账等多种支付方式。充值后按实际使用量扣费，余额永久有效。'
  },
  {
    question: '短信到达率如何保障？',
    answer: '我们与三大运营商建立直连通道，采用智能路由和多通道备份策略，确保99%以上的到达率。企业版客户享受99.9%的SLA保障。'
  },
  {
    question: '是否支持国际短信？',
    answer: '支持发送至全球200+个国家和地区。国际短信价格因目标地区而异，具体资费请联系客服获取价格表。'
  },
  {
    question: '短信内容有什么限制？',
    answer: '验证码和通知类短信无特殊限制。营销短信需要符合相关法规，禁止发送违法违规内容。我们提供内容审核服务确保合规。'
  },
  {
    question: '如何申请发票？',
    answer: '在账户管理中可以申请电子发票，支持个人和企业抬头。企业用户可申请增值税专用发票，发票将在3-5个工作日内开具。'
  },
  {
    question: '是否提供API文档和技术支持？',
    answer: '提供完整的API文档、SDK示例和技术支持。标准版和企业版用户还可享受在线客服和电话技术支持服务。'
  }
]

// 优势特性
const advantages = [
  {
    icon: BarChart3,
    title: '99.9% 高到达率',
    description: '三大运营商直连，多通道智能路由'
  },
  {
    icon: Clock,
    title: '秒级响应',
    description: '平均响应时间<100ms，极速触达用户'
  },
  {
    icon: Shield,
    title: '安全可靠',
    description: 'ISO27001认证，数据加密传输存储'
  },
  {
    icon: Users,
    title: '专业服务',
    description: '7×24小时技术支持，专属客户经理'
  }
]

export default function PricingPage() {
  const renderFeatureValue = (value: boolean | string) => {
    if (value === true) {
      return <CheckCircle className="h-5 w-5 text-green-600" />
    } else if (value === false) {
      return <X className="h-5 w-5 text-gray-400" />
    } else {
      return <span className="text-sm text-gray-600">{value}</span>
    }
  }

  return (
    <div className="space-y-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center space-y-8">
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900">
              简单透明的
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">定价方案</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              按实际使用量付费，无隐藏费用，无最低消费限制。
              选择适合您业务需求的方案，随时可以升级。
            </p>
            
            <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>100条免费试用</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>按量计费无月费</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>随时升级降级</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>7×24小时支持</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 优势特性 */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {advantages.map((advantage, index) => {
            const Icon = advantage.icon
            return (
              <div key={index} className="text-center space-y-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full">
                  <Icon className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{advantage.title}</h3>
                <p className="text-gray-600">{advantage.description}</p>
              </div>
            )
          })}
        </div>
      </section>

      {/* 价格方案 */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
            选择适合的方案
          </h2>
          <p className="text-xl text-gray-600">
            从个人开发者到大型企业，我们都有合适的解决方案
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pricingPlans.map((plan, index) => {
            const Icon = plan.icon
            return (
              <Card key={index} className={`relative ${plan.popular ? 'border-blue-500 shadow-xl ring-1 ring-blue-500 scale-105' : 'border-gray-200'} hover:shadow-lg transition-all duration-200`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-1.5 text-sm font-medium">
                      <Star className="h-3 w-3 mr-1" />
                      最受欢迎
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="text-center pb-8">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mx-auto mb-4">
                    <Icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <CardTitle className="text-2xl font-bold">{plan.name}</CardTitle>
                  <CardDescription className="text-base text-gray-600">{plan.description}</CardDescription>
                  <div className="mt-6">
                    <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                    {plan.period && <span className="text-gray-600 ml-2 text-lg">{plan.period}</span>}
                  </div>
                  <div className="text-sm text-gray-500 mt-2">{plan.monthlyQuota}</div>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="text-sm font-medium text-gray-900">包含功能：</div>
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start space-x-3">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                    
                    {plan.limitations.length > 0 && (
                      <>
                        <div className="text-sm font-medium text-gray-900 mt-6">限制条件：</div>
                        {plan.limitations.map((limitation, limitIndex) => (
                          <div key={limitIndex} className="flex items-start space-x-3">
                            <X className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-500">{limitation}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                  
                  <Button 
                    className={`w-full ${plan.popular ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700' : ''}`}
                    variant={plan.buttonVariant}
                    size="lg"
                    asChild
                  >
                    <Link to="/register">
                      {plan.buttonText}
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            )
          })}
        </div>

        <div className="text-center mt-8 text-gray-600">
          <p className="text-sm">
            所有方案均包含完整API文档和技术支持 • 
            <Link to="/contact" className="text-blue-600 hover:underline ml-1">
              需要定制方案？联系我们
            </Link>
          </p>
        </div>
      </section>

      {/* 功能对比 */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
            详细功能对比
          </h2>
          <p className="text-xl text-gray-600">
            了解各版本的具体功能差异和服务内容
          </p>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-6 py-5 text-left text-sm font-semibold text-gray-900">功能特性</th>
                  <th className="px-6 py-5 text-center text-sm font-semibold text-gray-900">体验版</th>
                  <th className="px-6 py-5 text-center text-sm font-semibold text-gray-900 bg-blue-50 relative">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500"></div>
                    标准版
                  </th>
                  <th className="px-6 py-5 text-center text-sm font-semibold text-gray-900">企业版</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {featureComparison.map((category, categoryIndex) => (
                  <React.Fragment key={categoryIndex}>
                    <tr className="bg-gray-25">
                      <td colSpan={4} className="px-6 py-4 text-sm font-semibold text-gray-900 bg-gray-50 border-t-2 border-gray-200">
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                          {category.category}
                        </div>
                      </td>
                    </tr>
                    {category.features.map((feature, featureIndex) => (
                      <tr key={featureIndex} className="hover:bg-gray-25 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{feature.name}</td>
                        <td className="px-6 py-4 text-center">{renderFeatureValue(feature.free)}</td>
                        <td className="px-6 py-4 text-center bg-blue-25">{renderFeatureValue(feature.standard)}</td>
                        <td className="px-6 py-4 text-center">{renderFeatureValue(feature.enterprise)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 常见问题 */}
      <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
            常见问题
          </h2>
          <p className="text-xl text-gray-600">
            解答您关心的问题，帮助您做出最佳选择
          </p>
        </div>
        
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <Card key={index} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold text-gray-900">{faq.question}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center space-y-8">
            <h2 className="text-3xl lg:text-5xl font-bold">
              准备开始了吗？
            </h2>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              立即注册获取100条免费短信，体验高效稳定的短信服务。
              有任何问题，我们的专业团队随时为您服务。
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-4 bg-white text-blue-600 hover:bg-gray-100 font-semibold"
                asChild
              >
                <Link to="/register">
                  <Zap className="mr-2 h-5 w-5" />
                  免费开始试用
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-blue-600 font-semibold"
              >
                <Headphones className="mr-2 h-5 w-5" />
                联系销售顾问
              </Button>
            </div>
            
            <div className="text-sm opacity-75 pt-8">
              <p>已服务超过10,000家企业客户 • 月发送量超过1亿条 • 99.9%服务可用性</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
